import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';

import { cn } from '@repo/lib/cn';
import { getFileExtension } from '@repo/lib/fs/getFileExtension';
import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { ChevronRightIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { FileIcon, FolderIcon } from '@/components/ui/file-icon';
import { ListItem, ListItemTitle } from '@/components/ui/list';

interface SelectableItemListProps {
  items: (DirectoryPublic | FilePublicWithPermissions)[];
  isItemSelected?: (item: DirectoryPublic | FilePublicWithPermissions) => boolean;
  isItemSelectable?: (item: DirectoryPublic | FilePublicWithPermissions) => boolean;
  onClickSelection: (item: DirectoryPublic | FilePublicWithPermissions, event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onNavigate: (directoryId: string) => void;
  multiple?: boolean;
  onDoubleClickSelection?: (item: DirectoryPublic | FilePublicWithPermissions) => Promise<void>;
}

export default function SelectableItemList(selectableItemListProps: SelectableItemListProps) {
  const { items, isItemSelected, isItemSelectable, onClickSelection, onNavigate, multiple, onDoubleClickSelection } = selectableItemListProps;
  return items.map((fsItem) => (
    <SelectableItemListItem
      key={fsItem.id}
      item={fsItem}
      isSelected={isItemSelected?.(fsItem)}
      canSelect={isItemSelectable?.(fsItem) ?? true}
      onClickSelection={onClickSelection}
      onNavigate={onNavigate}
      multiple={multiple}
      onDoubleClickSelection={onDoubleClickSelection}
    />
  ));
}

interface SelectableItemListItemProps {
  item: DirectoryPublic | FilePublicWithPermissions;
  isSelected?: boolean;
  canSelect?: boolean;
  onClickSelection: (item: DirectoryPublic | FilePublicWithPermissions, event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  onNavigate: (directoryId: string) => void;
  multiple?: boolean;
  onDoubleClickSelection?: (item: DirectoryPublic | FilePublicWithPermissions) => Promise<void>;
}

function SelectableItemListItem(selectableItemListItemProps: SelectableItemListItemProps) {
  const { item, isSelected, canSelect, onClickSelection, onNavigate, multiple, onDoubleClickSelection } = selectableItemListItemProps;

  const isFile = getIsFSEntityFile(item);

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    onClickSelection(item, event);
    if (!canSelect && !isFile) {
      handleNavigate();
    }
  };

  const handleDoubleClick = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (multiple === false && canSelect && onDoubleClickSelection) {
      event.preventDefault();
      event.stopPropagation();
      onClickSelection(item, event);
      await onDoubleClickSelection(item);
    }
  };

  const handleNavigate = () => {
    onNavigate(item.id);
  };

  return (
    <ListItem
      data-type={isFile ? 'file' : 'directory'}
      data-selected={isSelected}
      data-can-select={canSelect}
      className={cn(isSelected && 'bg-fill-primary-active/75', isFile && !canSelect && 'cursor-not-allowed', 'select-none')}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      <div className={cn('flex w-full items-center space-x-1', isFile && !canSelect && 'opacity-50')}>
        {isFile ? <FileIcon fileExtension={getFileExtension(item.name)} className="border-none bg-transparent" /> : <FolderIcon className="shrink border-none bg-transparent" />}

        <ListItemTitle>{item.name}</ListItemTitle>
        {!isFile && (
          <Button
            className="ml-auto"
            variant="tertiary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleNavigate();
            }}
          >
            <ChevronRightIcon />
            <span className="sr-only">Nawiguj do</span>
          </Button>
        )}
      </div>
    </ListItem>
  );
}
